import { useCallbackRef } from "@imwebme/clay-hooks";
import type { PropsWithChildren } from "react";
import { createContext, useContext, useMemo, useState } from "react";
import type { Nullable } from "../types";

const TooltipContext = createContext<
  | {
      isOpen: boolean;
      toggle: VoidFunction;
      setOff: VoidFunction;
      portalRef: Nullable<HTMLElement>;
      setPortalRef: (ref: HTMLElement | null) => void;
    }
  | undefined
>(undefined);

export const TooltipProvider = ({
  children,
  toggle,
  isOpen,
  setOff,
  onClose,
}: PropsWithChildren<{
  isOpen: boolean;
  toggle: VoidFunction;
  setOff: VoidFunction;
  onClose?: VoidFunction;
}>) => {
  const [portalRef, setPortalRef] = useState<Nullable<HTMLElement>>(null);

  const setOffProps = useCallbackRef(() => {
    setOff();
    onClose?.();
  });

  const memoContextValue = useMemo(() => {
    return {
      isOpen,
      toggle,
      setOff: setOffProps,
      portalRef,
      setPortalRef,
    };
  }, [isOpen, portalRef, setOffProps, toggle]);

  return <TooltipContext.Provider value={memoContextValue}>{children}</TooltipContext.Provider>;
};

export const useTooltipContext = () => {
  const context = useContext(TooltipContext);

  if (context === undefined) {
    throw new Error("useTooltipContext must be used within a TooltipProvider");
  }

  return context;
};
